import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #selectionGeneration = 0;
  #confirmedPrice;

  previewVariantPrice(variantId) {
    if (this.closest('product-card')) return;
    this.#restorePrice();
    const script = this.querySelector('script[data-variant-price-preview]');
    const container = this.querySelector('[ref="priceContainer"]');
    if (!script?.textContent || !container || !variantId) return;

    let prices;
    try {
      prices = JSON.parse(script.textContent);
    } catch {
      return;
    }
    const price = prices[String(variantId)];
    if (!price || price.eligible !== true) return;
    const regular = container.querySelector('.price__regular');
    const sale = container.querySelector('.price__sale');
    if (!regular || !sale || container.querySelector('.unit-price')) return;

    this.#confirmedPrice = container.cloneNode(true);
    for (const amount of container.querySelectorAll('.price')) amount.innerHTML = price.price;
    for (const amount of container.querySelectorAll('.compare-at-price')) amount.innerHTML = price.compareAt;
    regular.classList.toggle('price__hidden', price.onSale);
    sale.classList.toggle('price__hidden', !price.onSale);
    const discount = container.querySelector('[data-variant-discount]');
    if (discount) {
      discount.textContent = price.discountLabel || '';
      discount.hidden = !price.discountLabel;
    }
  }

  #restorePrice() {
    if (!this.#confirmedPrice) return;
    this.querySelector('[ref="priceContainer"]')?.replaceWith(this.#confirmedPrice);
    this.updatedCallback();
    this.#confirmedPrice = undefined;
  }

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    ++this.#selectionGeneration;
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (this.closest('product-card') || !(event.target instanceof Element) || event.target.closest('product-card')) return;

    if (event.target.dataset.productId && event.target.dataset.productId !== this.dataset.productId) return;
    const generation = ++this.#selectionGeneration;

    event.promise
      .then(({ detail }) => {
        if (generation !== this.#selectionGeneration) return;
        if (!detail?.html) {
          this.#restorePrice();
          return;
        }

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) {
          this.#restorePrice();
          return;
        }

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
          this.#confirmedPrice = undefined;
        } else {
          this.#restorePrice();
        }

        const oldPreview = this.querySelector('script[data-variant-price-preview]');
        const newPreview = newProductPrice.querySelector('script[data-variant-price-preview]');
        if (oldPreview && newPreview) oldPreview.replaceWith(newPreview);
        else if (newPreview) this.append(newPreview);
        else oldPreview?.remove();

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (generation !== this.#selectionGeneration) return;
        this.#restorePrice();
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

